/**
 * Create Provider User Dialog Component
 * 
 * Dialog for creating new provider users (platform staff)
 * Includes form validation with react-hook-form and Zod
 * Shows temporary password after successful creation
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Alert,
  CircularProgress,
  MenuItem,
  Typography,
  Paper,
} from '@mui/material';
import { AppButton, AppIconButton, AppTextField } from '../design-system';
import { Close as CloseIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { EmailField, PhoneField } from '../forms';
import { zodResolver } from '@hookform/resolvers/zod';
import { providerUsersService } from '../../services/ProviderUsersService';
import { CreateProviderUserRequestSchema } from '../../../../shared/src/schemas';
import type { z } from 'zod';
import * as dt from '../../../../shared/src/data.types';

// Use input type for form (before validation)
type CreateProviderUserFormData = z.input<typeof CreateProviderUserRequestSchema>;

interface CreateProviderUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CreateResult {
  email: string;
  temporaryPassword: string;
}

const CreateProviderUserDialog: React.FC<CreateProviderUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createResult, setCreateResult] = useState<CreateResult | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateProviderUserFormData>({
    resolver: zodResolver(CreateProviderUserRequestSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      role: dt.ALL_USER_ROLES.provider_support,
    },
  });

  const handleClose = () => {
    if (createResult) {
      // If user was created, trigger success callback
      onSuccess();
    }
    reset();
    setError(null);
    setCreateResult(null);
    onClose();
  };

  const onSubmit = async (data: CreateProviderUserFormData) => {
    try {
      setLoading(true);
      setError(null);

      // Validate and transform the data
      const validatedData = CreateProviderUserRequestSchema.parse(data);
      const result = await providerUsersService.createProviderUser(validatedData);

      // Show success with temporary password
      setCreateResult({
        email: result.user.email,
        temporaryPassword: result.temporaryPassword,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // If user was created successfully, show result screen
  if (createResult) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            User Created Successfully
            <AppIconButton edge="end" onClick={handleClose} size="small">
              <CloseIcon />
            </AppIconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 3 }}>
            The provider user has been created and an invitation email has been sent.
          </Alert>

          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Email Address
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography variant="body1" sx={{ flexGrow: 1, fontFamily: 'monospace' }}>
                {createResult.email}
              </Typography>
              <AppIconButton size="small" onClick={() => copyToClipboard(createResult.email)}>
                <CopyIcon fontSize="small" />
              </AppIconButton>
            </Box>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Temporary Password
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  flexGrow: 1,
                  fontFamily: 'monospace',
                  bgcolor: 'warning.light',
                  color: 'warning.contrastText',
                  p: 1,
                  borderRadius: 1,
                }}
              >
                {createResult.temporaryPassword}
              </Typography>
              <AppIconButton
                size="small"
                onClick={() => copyToClipboard(createResult.temporaryPassword)}
              >
                <CopyIcon fontSize="small" />
              </AppIconButton>
            </Box>
          </Paper>

          <Alert severity="info" sx={{ mt: 2 }}>
            Please save this temporary password. The user will be required to change it on first
            login.
          </Alert>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={handleClose} variant="contained">
            Done
          </AppButton>
        </DialogActions>
      </Dialog>
    );
  }

  // Normal create form
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Create Provider User
            <AppIconButton edge="end" onClick={handleClose} size="small" disabled={loading}>
              <CloseIcon />
            </AppIconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <EmailField
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={loading}
                  required
                />
              )}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    label="First Name"
                    required
                    fullWidth
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    disabled={loading}
                  />
                )}
              />

              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    label="Last Name"
                    required
                    fullWidth
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                    disabled={loading}
                  />
                )}
              />
            </Box>

            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <PhoneField
                  value={field.value ?? null}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  disabled={loading}
                />
              )}
            />

            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <AppTextField
                  {...field}
                  label="Role"
                  select
                  required
                  fullWidth
                  error={!!errors.role}
                  helperText={errors.role?.message}
                  disabled={loading}
                >
                  <MenuItem value="provider_admin">Administrator</MenuItem>
                  <MenuItem value="provider_engineer">Engineer</MenuItem>
                  <MenuItem value="provider_support">Support</MenuItem>
                  <MenuItem value="provider_deployment_engineer">Deployment Engineer (contractor)</MenuItem>
                </AppTextField>
              )}
            />
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            A temporary password will be generated and sent to the user's email address. The user
            will be required to change it on first login.
          </Alert>
        </DialogContent>

        <DialogActions>
          <AppButton onClick={handleClose} disabled={loading}>
            Cancel
          </AppButton>
          <AppButton type="submit" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Create User'}
          </AppButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateProviderUserDialog;
